import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Siren, MapPin, Clock, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { api, gbp, ago } from "../lib/api";
import { useAuth, catName } from "../context/AuthContext";

const left = (until) => {
  const h = Math.max(0, Math.round((new Date(until) - Date.now()) / 36e5));
  return h >= 24 ? `${Math.round(h / 24)}d left` : `${h}h left`;
};

export const UrgentBadge = ({ until }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-rose-600 text-white text-[11px] font-bold uppercase tracking-wide px-2.5 py-1" data-testid="urgent-badge">
    <Siren size={12} />Urgent{until && <span className="font-medium normal-case tracking-normal opacity-90">· {left(until)}</span>}
  </span>
);

export const jobLink = (user, id) => (user?.role === "provider" ? `/provider/jobs/${id}` : `/login?next=${encodeURIComponent(`/provider/jobs/${id}`)}`);

export function UrgentJobCard({ j, active, onHover }) {
  const { user, cats } = useAuth();
  return (
    <Link to={jobLink(user, j.id)} onMouseEnter={() => onHover?.(j.id)} className={`card-hover p-5 flex flex-col border-l-4 border-l-rose-500 ${active ? "ring-2 ring-rose-400" : ""}`} data-testid={`urgent-job-card-${j.id}`}>
      <div className="flex items-center justify-between gap-2"><UrgentBadge until={j.urgent_until} /><span className="text-xs text-slate-500 flex items-center gap-1"><Clock size={11} />{ago(j.created_at)}</span></div>
      <h3 className="font-bold text-slate-900 mt-3 line-clamp-2">{j.title}</h3>
      <p className="text-sm text-slate-600 mt-1 line-clamp-2">{j.description}</p>
      <div className="mt-auto pt-3 text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
        <span className="font-medium text-slate-700">{catName(cats, j.category)}</span>
        <span className="flex items-center gap-1"><MapPin size={11} />{j.town || j.postcode_area} {j.postcode_area}</span>
        <span>{j.budget_label}</span><span>{j.application_count} applicants</span>
      </div>
    </Link>
  );
}

export function UrgentPicker({ options, prices, value, onChange, allowNone = true }) {
  return (
    <div className="grid sm:grid-cols-2 gap-2" data-testid="urgent-picker">
      {allowNone && <button type="button" onClick={() => onChange("")} className={`rounded-xl border-2 px-4 py-3 text-left transition-colors ${!value ? "border-[#374B5C] bg-slate-50" : "border-slate-200 hover:border-slate-300"}`} data-testid="urgent-option-none">
        <b className="text-slate-900 text-sm">Standard listing</b><span className="block text-xs text-slate-500">Free</span></button>}
      {options.map((o) => (
        <button key={o.key} type="button" onClick={() => onChange(o.key)} className={`rounded-xl border-2 px-4 py-3 text-left flex justify-between items-center transition-colors ${value === o.key ? "border-rose-500 bg-rose-50" : "border-slate-200 hover:border-slate-300"}`} data-testid={`urgent-option-${o.key}`}>
          <span><b className="text-slate-900 text-sm">Urgent for {o.name}</b><span className="block text-xs text-slate-500">Top of pros' lists + instant alerts</span></span>
          <b className="text-rose-700">{gbp(prices?.[o.key] ?? o.price)}</b>
        </button>))}
    </div>
  );
}

const ROTATE_MS = 6000;

function RotateControls({ pages, page, go }) {
  return (
    <div className="flex items-center justify-center gap-3 mt-6" data-testid="home-urgent-controls">
      <button onClick={() => go(page - 1)} className="p-2 rounded-full border border-slate-200 hover:bg-slate-50" aria-label="Previous urgent jobs" data-testid="home-urgent-prev"><ChevronLeft size={16} /></button>
      {Array.from({ length: pages }, (_, i) => (
        <button key={i} onClick={() => go(i)} aria-label={`Show urgent jobs set ${i + 1}`} className={`h-2 rounded-full transition-[width,background-color] duration-300 ${i === page ? "w-6 bg-rose-500" : "w-2 bg-slate-300"}`} data-testid={`home-urgent-dot-${i}`} />))}
      <button onClick={() => go(page + 1)} className="p-2 rounded-full border border-slate-200 hover:bg-slate-50" aria-label="Next urgent jobs" data-testid="home-urgent-next"><ChevronRight size={16} /></button>
    </div>
  );
}

export function UrgentJobsHome() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState(null);
  const [page, setPage] = useState(0);
  const paused = useRef(false);
  useEffect(() => { api.get("/urgent-jobs", { params: { limit: 24 } }).then((r) => setJobs(r.data)).catch(() => setJobs([])); }, []);
  const pages = Math.ceil((jobs?.length || 0) / 4);
  useEffect(() => {
    if (pages < 2) return undefined;
    const t = setInterval(() => { if (!paused.current) setPage((p) => (p + 1) % pages); }, ROTATE_MS);
    return () => clearInterval(t);
  }, [pages]);
  if (!jobs) return null;
  const go = (i) => setPage((i + pages) % pages);
  return (
    <section className="container-x py-16 lg:py-20" data-testid="home-urgent-jobs">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div><span className="eyebrow !text-rose-600">Urgent jobs</span><h2 className="h2 mt-2">Homeowners who need help fast</h2><p className="muted mt-2">Professionals: these customers want someone as soon as possible.</p></div>
        {jobs.length > 0 && <Link to="/urgent-jobs" className="btn-navy" data-testid="home-urgent-view-more">View more <ArrowRight size={16} /></Link>}
      </div>
      {jobs.length === 0 ? (
        <div className="card p-8 flex flex-col sm:flex-row sm:items-center gap-4 border-l-4 border-l-rose-500" data-testid="home-urgent-empty">
          <Siren className="text-rose-500 shrink-0" size={28} />
          <div className="flex-1"><b className="text-slate-900">No urgent jobs right now</b><p className="text-sm muted mt-1">Need someone fast? Post your job and make it urgent so local professionals see it first and get an instant alert.</p></div>
          {user?.role !== "provider" && <Link to="/post-job" className="btn-coral" data-testid="home-urgent-post-btn">Post an urgent job</Link>}
        </div>
      ) : (
        <div onMouseEnter={() => { paused.current = true; }} onMouseLeave={() => { paused.current = false; }}>
          <div key={page} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 stagger" data-testid="home-urgent-page">{jobs.slice(page * 4, page * 4 + 4).map((j) => <UrgentJobCard key={j.id} j={j} />)}</div>
          {pages > 1 && <RotateControls pages={pages} page={page} go={go} />}
        </div>
      )}
    </section>
  );
}
